import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { PrimaryButton, SecondaryButton } from '../components/buttons';
import { useWizard } from '../context/WizardCtx';
import { saveDraft } from '../utils/draftStorage';
import { parseCsvToDayColumns } from '../utils/parseCsv';
import { startOptimization } from '../utils/optimizerApi';
import { useOptimizationPolling } from '../hooks/useOptimizationPolling';
import { OptimizerErrorModal } from '../components/OptimizerErrorModal';
import { DayColumn, CadastroRow, ClienteLimit, CotaDiaria } from '../types/wizard';
import { Save, Home, Zap, Clock, CheckCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * ScheduleGenerationStep props
 */
interface ScheduleGenerationStepProps {
    onScheduleGenerated: (schedule: DayColumn[]) => void;
    onGoHome?: () => void;
}

/**
 * Daily Quotas Table Component with collapsible vendor sections
 */
interface DailyQuotasTableProps {
    quotas: CotaDiaria[];
}

const DailyQuotasTable: React.FC<DailyQuotasTableProps> = ({ quotas }) => {
    const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());

    // Group quotas by vendor
    const quotasByVendor = quotas.reduce((acc, quota) => {
        if (!acc[quota.no_vendedor]) {
            acc[quota.no_vendedor] = [];
        }
        acc[quota.no_vendedor].push(quota);
        return acc;
    }, {} as Record<string, CotaDiaria[]>);

    // Sort quotas by date within each vendor (string comparison for YYYY-MM-DD format)
    Object.keys(quotasByVendor).forEach(vendor => {
        quotasByVendor[vendor].sort((a, b) => {
            // Simple string comparison works perfectly for YYYY-MM-DD format
            // Avoids timezone issues with Date parsing
            return a.data.localeCompare(b.data);
        });


    });

    const toggleVendor = (vendor: string) => {
        const newExpanded = new Set(expandedVendors);
        if (newExpanded.has(vendor)) {
            newExpanded.delete(vendor);
        } else {
            newExpanded.add(vendor);
        }
        setExpandedVendors(newExpanded);
    };

    const vendors = Object.keys(quotasByVendor).sort();

    return (
        <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="mb-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Cotas Diárias Calculadas ({quotas.length} entradas)
                </h4>
                <p className="text-xs text-gray-500">
                    Clique nos vendedores para expandir e verificar as cotas por dia
                </p>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
                {vendors.map((vendor, vendorIndex) => {
                    const vendorQuotas = quotasByVendor[vendor];
                    const totalQuota = vendorQuotas.reduce((sum, q) => sum + q.cota, 0);
                    const isExpanded = expandedVendors.has(vendor);

                    return (
                        <div key={vendor} className={vendorIndex > 0 ? 'border-t border-gray-200' : ''}>
                            {/* Vendor Header */}
                            <button
                                onClick={() => toggleVendor(vendor)}
                                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left flex items-center justify-between"
                            >
                                <div className="flex items-center gap-3">
                                    {isExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-500" />
                                    )}
                                    <div>
                                        <span className="font-semibold text-gray-900 text-sm">
                                            {vendor}
                                        </span>
                                        <div className="text-xs text-gray-500">
                                            {vendorQuotas.length} dias • {vendorQuotas[0]?.filial}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-semibold text-blue-600">
                                        {totalQuota.toFixed(2)} m³
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        Total do mês
                                    </div>
                                </div>
                            </button>

                            {/* Vendor Quotas (Expandable) */}
                            {isExpanded && (
                                <div className="bg-white">
                                    <div className="px-4 py-2 bg-gray-25 border-b border-gray-100">
                                        <div className="grid grid-cols-3 gap-4 text-xs font-medium text-gray-600">
                                            <div>Data</div>
                                            <div className="text-right">Cota (m³)</div>
                                            <div className="text-right">% do Mês</div>
                                        </div>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {vendorQuotas.map((quota, index) => (
                                            <div
                                                key={`${quota.no_vendedor}-${quota.data}`}
                                                className={`px-4 py-2 grid grid-cols-3 gap-4 text-xs hover:bg-gray-50 ${index < vendorQuotas.length - 1 ? 'border-b border-gray-100' : ''
                                                    } ${quota.cota === 0 ? 'opacity-60' : ''}`}
                                            >
                                                <div className="text-gray-700">
                                                    {quota.data.split('-').reverse().join('/')}
                                                </div>
                                                <div className={`text-right font-semibold ${quota.cota === 0 ? 'text-gray-400' : 'text-blue-600'
                                                    }`}>
                                                    {quota.cota.toFixed(2)}
                                                </div>
                                                <div className="text-right text-gray-500">
                                                    {totalQuota > 0 ? ((quota.cota / totalQuota) * 100).toFixed(1) : '0.0'}%
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Summary */}
            <div className="mt-3 text-xs text-gray-500 text-center">
                {vendors.length} vendedores • {quotas.length} entradas de cota •
                Total geral: {quotas.reduce((sum, q) => sum + q.cota, 0).toFixed(2)} m³
            </div>


        </div>
    );
};

/**
 * Get client limits from cadastro data
 */
const getLimitesFromCadastro = (cadastro?: CadastroRow[]): ClienteLimit[] => {
    if (!cadastro) return [];

    return cadastro.map(row => ({
        codigo: row.codigoCliente,
        nome: row.nomeCliente,
        vendedor: row.nomeVendedor,
        limite: typeof row.limiteVolume === 'number' ? row.limiteVolume :
            typeof row.limiteVolume === 'string' && !isNaN(parseFloat(row.limiteVolume)) ?
                parseFloat(row.limiteVolume) : 1000,
        limiteDia: row.limiteDia,
        limiteVolume: row.limiteVolume
    }));
};

/**
 * Step 3: Schedule Generation Step - Generate optimized schedule or manual CSV upload
 */
export const ProductionUploadStep: React.FC<ScheduleGenerationStepProps> = ({
    onScheduleGenerated,
    onGoHome,
}) => {
    const { data, updateVendedorQuotas } = useWizard();

    // Debug: Log data received from previous step
    useEffect(() => {
        console.log('📊 ProductionUploadStep - Data received:', {
            vendorQuotas: data.cota_vendedores,
            dailyQuotas: data.cota_diaria?.length || 0,
            maxDailyVolume: data.maxDailyVolume
        });
    }, [data]);
    const [isSaving, setIsSaving] = useState(false);
    const [isOptimizing, setIsOptimizing] = useState(false); // New state for immediate button disable
    const [optimizerError, setOptimizerError] = useState<{
        message: string;
        details?: string[];
    } | null>(null);

    // Local state for percentage editing
    const [percentageInputs, setPercentageInputs] = useState<{ [index: number]: string }>({});

    // Use the optimization polling hook
    const { state: pollingState, startPolling, resetState } = useOptimizationPolling();

    // Monitor polling state changes
    useEffect(() => {
        // Re-enable button when polling starts (after initial API call)
        if (pollingState.isPolling && isOptimizing) {
            // Add a small delay to show the loading state briefly
            setTimeout(() => {
                setIsOptimizing(false);
            }, 1500); // Show button disabled for 1.5 seconds
        }

        if (pollingState.status === 'COMPLETED' && pollingState.csvData) {
            // Process the CSV data and generate schedule
            try {
                const schedule = parseCsvToDayColumns(pollingState.csvData);

                if (schedule.length === 0) {
                    throw new Error('Nenhum dado válido encontrado no CSV otimizado');
                }

                toast.success('✅ Cronograma otimizado gerado com sucesso!');
                console.log('✅ Optimization completed successfully');

                // Open the schedule chart with the optimized data
                onScheduleGenerated(schedule);

            } catch (parseError) {
                console.error('❌ Error parsing optimized CSV:', parseError);
                setOptimizerError({
                    message: parseError instanceof Error ?
                        `Erro ao processar CSV otimizado: ${parseError.message}` :
                        'Erro ao processar CSV otimizado'
                });
            }
        } else if (pollingState.status === 'ERROR') {
            setOptimizerError({
                message: pollingState.error || 'Erro desconhecido na otimização'
            });
            setIsOptimizing(false); // Reset state on error
        } else if (pollingState.status === 'TIMEOUT') {
            setOptimizerError({
                message: pollingState.error || 'Tempo de otimização excedido'
            });
            setIsOptimizing(false); // Reset state on timeout
        }
    }, [pollingState.status, pollingState.csvData, pollingState.error, onScheduleGenerated]);

    /**
     * Handle percentage input change (local state only)
     */
    const handlePercentageInputChange = (vendorIndex: number, value: string) => {
        setPercentageInputs(prev => ({
            ...prev,
            [vendorIndex]: value
        }));
    };

    /**
     * Handle percentage input blur (commit to vendor state)
     */
    const handlePercentageInputBlur = (vendorIndex: number, value: string) => {
        const numericValue = parseFloat(value) || 0;

        // Update vendor quotas
        const updatedVendors = [...data.cota_vendedores];
        updatedVendors[vendorIndex] = {
            ...updatedVendors[vendorIndex],
            fatia_percentual: numericValue
        };

        updateVendedorQuotas(updatedVendors);

        // Clean up local input state
        setPercentageInputs(prev => {
            const updated = { ...prev };
            delete updated[vendorIndex];
            return updated;
        });
    };

    /**
     * Get display value for percentage input
     */
    const getPercentageDisplayValue = (vendorIndex: number, vendor: any): string => {
        // If there's local input state, use it
        if (percentageInputs[vendorIndex] !== undefined) {
            return percentageInputs[vendorIndex];
        }

        // Otherwise, use the vendor's actual value
        return vendor.fatia_percentual === 0 ? '' : vendor.fatia_percentual.toString();
    };



    /**
     * Save draft to local storage
     */
    const handleSaveDraft = async () => {
        console.log('💾 ProductionUploadStep - handleSaveDraft called');
        setIsSaving(true);
        try {
            console.log('💾 Current data:', data);
            const draftData = { ...data, savedStep: 3 as const };
            console.log('💾 Draft data to save:', draftData);

            await saveDraft(draftData);
            console.log('💾 Draft saved successfully');
            toast.success('Rascunho salvo com sucesso');
        } catch (error) {
            console.error('💾 Error saving draft:', error);
            toast.error('Falha ao salvar rascunho');
        } finally {
            setIsSaving(false);
        }
    };



    /**
     * Start async optimization workflow
     */
    const handleOptimizeWorkflow = async () => {
        // Disable button immediately to prevent multiple clicks
        setIsOptimizing(true);

        try {
            // Validate required data
            if (!data.cota_diaria || data.cota_diaria.length === 0) {
                toast.error('Configure os vendedores e suas cotas diárias primeiro. Faça upload da planilha de produção.');
                return;
            }

            if (!data.cadastro || data.cadastro.length === 0) {
                toast.error('Configure o cadastro de vendedores e clientes no Passo 1');
                return;
            }

            // Reset any previous state
            setOptimizerError(null);
            resetState();

            console.log('🚀 Starting async optimization...');

            // Start the optimization process
            const taskResponse = await startOptimization(
                data.cota_diaria,
                data.cadastro || []
            );

            console.log('✅ Optimization started with task_id:', taskResponse.task_id);

            // Save task_id to sessionStorage for later use
            sessionStorage.setItem('dexco-task-id', taskResponse.task_id);

            // Start polling for progress
            startPolling(taskResponse.task_id);

        } catch (error) {
            console.error('❌ Error starting optimization:', error);
            setOptimizerError({
                message: error instanceof Error ? error.message : 'Erro ao iniciar otimização'
            });
            // Re-enable button on error
            setIsOptimizing(false);
        }
    };

    /**
     * Retry optimizer workflow
     */
    const handleRetryOptimizer = () => {
        setOptimizerError(null);
        setIsOptimizing(false); // Reset optimization state
        resetState();
        handleOptimizeWorkflow();
    };

    // Calculate summary statistics
    const totalVendors = data.cota_vendedores.length;
    const clienteLimits = getLimitesFromCadastro(data.cadastro);
    const totalClients = clienteLimits.length;
    const totalPercentage = data.cota_vendedores.reduce((sum, vendor) => sum + vendor.fatia_percentual, 0);
    const hasProductionFile = !!data.producao_planejada;

    // Check if optimizer should be disabled
    const isOptimizerDisabled = isOptimizing ||
        pollingState.isPolling ||
        !data.cota_diaria ||
        data.cota_diaria.length === 0;

    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
        >
            {/* Header */}
            <div className="text-center">
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-dexco-green-primary rounded-lg flex items-center justify-center">
                        <Zap className="w-6 h-6 text-white" />
                    </div>
                </div>
                <h2 className="font-display font-semibold text-2xl text-dexco-text-dark mb-2">
                    Gerar Cronograma Otimizado
                </h2>
                <p className="text-gray-600 font-body">
                    Execute a otimização automática ou carregue um CSV manualmente para visualização
                </p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Vendors Summary */}
                <div className="bg-dexco-white p-6 rounded-lg border border-gray-200 max-h-[65vh] sm:max-h-[70vh] md:max-h-[65vh] flex flex-col">
                    <div className="flex items-center gap-3 mb-4 flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-body font-semibold text-dexco-text-dark">
                                Cotas por Vendedor
                            </h3>
                            <p className="text-sm text-gray-600">
                                {totalVendors} vendedor{totalVendors !== 1 ? 'es' : ''} configurado{totalVendors !== 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>

                    <div className={`p-3 rounded-lg mb-4 flex-shrink-0 ${Math.abs(totalPercentage - 100) < 0.01
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                        }`}>
                        <div className="flex justify-between items-center">
                            <span className="font-body font-medium text-gray-700">Total das Fatias:</span>
                            <span className={`font-display font-bold ${Math.abs(totalPercentage - 100) < 0.01 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                {totalPercentage.toFixed(2)}%
                            </span>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2">
                        {data.cota_vendedores.map((vendor, index) => (
                            <div key={index} className="flex justify-between items-center text-sm gap-3">
                                <span className="font-body text-gray-700 flex-1 truncate">{vendor.vendedor}</span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <input
                                        type="number"
                                        value={getPercentageDisplayValue(index, vendor)}
                                        onChange={(e) => handlePercentageInputChange(index, e.target.value)}
                                        onBlur={(e) => handlePercentageInputBlur(index, e.target.value)}
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        placeholder="0.00"
                                        className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-dexco-green-primary focus:border-transparent text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                    <span className="text-xs text-gray-500">%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Clients Summary */}
                <div className="bg-dexco-white p-6 rounded-lg border border-gray-200 max-h-[65vh] sm:max-h-[70vh] md:max-h-[65vh] flex flex-col">
                    <div className="flex items-center gap-3 mb-4 flex-shrink-0">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-body font-semibold text-dexco-text-dark">
                                Cadastro de Clientes
                            </h3>
                            <p className="text-sm text-gray-600">
                                {totalClients} cliente{totalClients !== 1 ? 's' : ''} configurado{totalClients !== 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2">
                        {clienteLimits.map((client, index) => (
                            <div key={index} className="flex justify-between items-center text-sm">
                                <span className="font-body text-gray-700 truncate mr-2">{client.nome}</span>
                                <span className="font-body font-semibold text-purple-600 whitespace-nowrap">
                                    {typeof client.limiteVolume === 'string' ? client.limiteVolume : `${client.limite} m³`}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Production File Status */}
            <div className="bg-dexco-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasProductionFile
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="font-body font-semibold text-dexco-text-dark">
                            Arquivo de Produção
                        </h3>
                        <p className="text-sm text-gray-600">
                            {hasProductionFile
                                ? `✅ ${data.producao_planejada?.name}`
                                : '❌ Arquivo obrigatório não enviado'
                            }
                        </p>
                    </div>
                </div>

                {/* Daily Quotas Table - Collapsible by Vendor */}
                {data.cota_diaria && data.cota_diaria.length > 0 && (
                    <DailyQuotasTable quotas={data.cota_diaria} />
                )}
            </div>



            {/* Optimization Progress */}
            {pollingState.isPolling && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-dexco-white p-6 rounded-lg border border-gray-200"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            {pollingState.status === 'PENDING' && (
                                <Clock className="w-5 h-5 text-blue-500 animate-pulse" />
                            )}
                            {pollingState.status === 'RUNNING' && (
                                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            )}
                            <span className="font-body font-semibold text-dexco-text-dark">
                                {pollingState.progress.currentStep}
                            </span>
                        </div>
                        <span className="text-sm text-gray-600">
                            {pollingState.progress.percentage}%
                        </span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <motion.div
                            className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"
                            initial={{ width: '0%' }}
                            animate={{ width: `${pollingState.progress.percentage}%` }}
                            transition={{ duration: 0.5 }}
                        />
                    </div>

                    <p className="text-sm text-gray-600 mt-2">
                        Processando {totalVendors} vendedores e {totalClients} clientes...
                    </p>
                </motion.div>
            )}

            {/* Success/Error Status */}
            {pollingState.status === 'COMPLETED' && !pollingState.isPolling && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-50 p-6 rounded-lg border border-green-200"
                >
                    <div className="flex items-center gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        <div>
                            <h3 className="font-body font-semibold text-green-800">
                                Otimização Concluída!
                            </h3>
                            <p className="text-green-600 text-sm">
                                Cronograma otimizado gerado com sucesso.
                            </p>
                        </div>
                    </div>
                </motion.div>
            )}

            {(pollingState.status === 'ERROR' || pollingState.status === 'TIMEOUT') && !pollingState.isPolling && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 p-6 rounded-lg border border-red-200"
                >
                    <div className="flex items-center gap-3">
                        <XCircle className="w-6 h-6 text-red-600" />
                        <div>
                            <h3 className="font-body font-semibold text-red-800">
                                Erro na Otimização
                            </h3>
                            <p className="text-red-600 text-sm">
                                {pollingState.error || 'Erro desconhecido'}
                            </p>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Footer Actions */}
            <div className="flex flex-col gap-4 pt-6">
                {/* Main Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <SecondaryButton
                            onClick={handleSaveDraft}
                            isLoading={isSaving}
                            disabled={pollingState.isPolling}
                            leftIcon={<Save className="w-4 h-4" />}
                            aria-label="Salvar configurações atuais como rascunho"
                        >
                            Salvar Rascunho
                        </SecondaryButton>

                        {onGoHome && (
                            <SecondaryButton
                                onClick={onGoHome}
                                disabled={pollingState.isPolling}
                                leftIcon={<Home className="w-4 h-4" />}
                                aria-label="Voltar para a página inicial"
                            >
                                Voltar ao Início
                            </SecondaryButton>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        {/* Optimizer Button - Primary Action */}
                        <PrimaryButton
                            onClick={handleOptimizeWorkflow}
                            disabled={isOptimizerDisabled}
                            isLoading={isOptimizing || pollingState.isPolling}
                            leftIcon={<Zap className="w-4 h-4" />}
                            className="min-w-[200px] bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
                        >
                            {isOptimizing ? 'Iniciando...' :
                                pollingState.isPolling ? 'Otimizando...' :
                                    'Otimizar Cronograma'}
                        </PrimaryButton>


                    </div>
                </div>

                {/* Help Text */}
                <div className="text-center text-sm text-gray-600">
                    <p>
                        <strong>Otimizar Cronograma:</strong> Integra com o otimizador da MT2Data para gerar cronograma de priorizações
                    </p>
                </div>
            </div>

            {/* Optimizer Error Modal */}
            <OptimizerErrorModal
                isOpen={!!optimizerError}
                error={optimizerError || { message: '' }}
                onClose={() => setOptimizerError(null)}
                onRetry={handleRetryOptimizer}
            />
        </motion.div>
    );
}; 